
import { useState } from 'react';
import { Link } from 'react-router-dom';
import SiteSearch from '../../components/feature/SiteSearch';

export default function DrugPriceSearch() {
  const [searchTerm, setSearchTerm] = useState('');
  const [searchType, setSearchType] = useState('name');

  const quickLinks = [
    {
      title: '薬価サーチ',
      description: '医薬品の薬価情報を素早く検索できる専門サイト',
      url: 'https://yakka-search.com/',
      icon: 'ri-search-line',
      color: 'bg-blue-50 text-blue-600'
    },
    {
      title: '薬価基準収載品目リスト',
      description: '厚生労働省の公式薬価基準データベース',
      url: 'https://www.mhlw.go.jp/topics/2021/04/tp20210401-01.html',
      icon: 'ri-government-line',
      color: 'bg-green-50 text-green-600'
    },
    {
      title: '薬価改定情報',
      description: '最新の薬価改定に関する情報',
      url: 'https://www.mhlw.go.jp/stf/seisakunitsuite/bunya/kenkou_iryou/iryouhoken/yakuzai/index.html',
      icon: 'ri-refresh-line',
      color: 'bg-purple-50 text-purple-600'
    },
    {
      title: '後発医薬品薬価',
      description: 'ジェネリック医薬品の薬価情報',
      url: 'https://www.mhlw.go.jp/stf/seisakunitsuite/bunya/kenkou_iryou/iryou/kouhatu-iyaku/index.html',
      icon: 'ri-medicine-bottle-line',
      color: 'bg-orange-50 text-orange-600'
    }
  ];

  const searchCategories = [
    {
      id: 'name',
      label: '薬品名',
      placeholder: '薬品名を入力してください（例：アムロジピン錠）'
    },
    {
      id: 'ingredient',
      label: '成分名',
      placeholder: '成分名を入力してください（例：amlodipine）'
    },
    {
      id: 'code',
      label: '薬価基準収載医薬品コード',
      placeholder: 'コードを入力してください（例：2171022F1）'
    },
    {
      id: 'company',
      label: '製薬会社名',
      placeholder: '会社名を入力してください（例：ファイザー）'
    }
  ];

  const handleSearch = () => {
    if (searchTerm.trim()) {
      const searchUrl = 'https://yakka-search.com/';
      window.open(searchUrl, '_blank');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <Link to="/" className="flex items-center">
                <div className="w-10 h-10 mr-3 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-500 to-emerald-500 rounded-lg transform rotate-12 shadow-lg"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-lg"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-600" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </Link>
            </div>
            <div className="flex items-center space-x-6">
              <div className="hidden lg:block">
                <SiteSearch />
              </div>
              <nav className="hidden md:flex space-x-8">
                <Link to="/services" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">サービス</Link>
                <Link to="/health-support" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">健康サポート</Link>
                <Link to="/pharmacist-resources" className="text-teal-600 hover:text-teal-700 font-medium whitespace-nowrap cursor-pointer">薬剤師向け</Link>
                <Link to="/information" className="text-gray-700 hover:text-teal-600 font-medium whitespace-nowrap cursor-pointer">情報提供</Link>
              </nav>
            </div>
            <button className="md:hidden p-2">
              <i className="ri-menu-line text-xl"></i>
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Search */}
      <div className="lg:hidden bg-gray-50 px-4 py-3 border-b">
        <SiteSearch />
      </div>

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-green-50 to-emerald-50 py-16">
        <div className="absolute inset-0">
          <img 
            src="https://public.readdy.ai/ai/img_res/2e24e16e9f8ecb848c284826c325e8c7.jpg"
            alt="薬価サーチ"
            className="w-full h-full object-cover object-top opacity-20"
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
            <span className="text-green-600">薬価</span>サーチ
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            医薬品の薬価情報を素早く検索・確認できる総合データベース
          </p>
        </div>
      </section>

      {/* Search Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
            <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">薬価検索</h2>
            
            {/* Search Type Selection */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-3">検索方法を選択</label>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {searchCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setSearchType(category.id)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap cursor-pointer ${
                      searchType === category.id
                        ? 'bg-green-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-green-50 hover:text-green-600'
                    }`}
                  >
                    {category.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Search Input */}
            <div className="mb-6">
              <div className="relative">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={searchCategories.find(cat => cat.id === searchType)?.placeholder}
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-sm"
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                />
                <button
                  onClick={handleSearch}
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 w-8 h-8 bg-green-600 text-white rounded-lg flex items-center justify-center hover:bg-green-700 transition-colors cursor-pointer"
                >
                  <i className="ri-search-line"></i>
                </button>
              </div>
            </div>

            <div className="text-center">
              <p className="text-sm text-gray-500">
                検索ボタンをクリックすると薬価サーチサイトが開きます
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Links */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-center text-gray-900 mb-12">クイックアクセス</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickLinks.map((link, index) => (
              <a
                key={index}
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white rounded-xl shadow-lg hover:shadow-xl transition-shadow p-6 border border-gray-100 cursor-pointer"
              >
                <div className={`w-12 h-12 rounded-lg ${link.color} flex items-center justify-center mb-4`}>
                  <i className={`${link.icon} text-xl`}></i>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">{link.title}</h3>
                <p className="text-gray-600 text-sm">{link.description}</p>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 mr-2 relative">
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-400 to-emerald-400 rounded-lg transform rotate-12"></div>
                  <div className="absolute inset-0 bg-gradient-to-br from-teal-300 to-emerald-300 rounded-lg flex items-center justify-center">
                    <i className="ri-heart-pulse-fill text-white text-sm"></i>
                  </div>
                </div>
                <div className="text-xl font-bold text-teal-400" style={{ fontFamily: '"Pacifico", serif' }}>
                  みんなの薬局～Pharmacare～
                </div>
              </div>
              <p className="text-gray-400">
                患者さんと薬剤師をつなぐ総合医療情報プラットフォーム
              </p>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">検索ツール</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/package-insert-search" className="hover:text-white cursor-pointer">添付文書検索</Link></li>
                <li><Link to="/drug-price-search" className="hover:text-white cursor-pointer">薬価サーチ</Link></li>
                <li><a href="https://yakka-search.com/" target="_blank" rel="noopener noreferrer" className="hover:text-white cursor-pointer">薬価サーチ（外部）</a></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">薬剤師向け</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/pharmacist-resources" className="hover:text-white cursor-pointer">専門リソース</Link></li>
                <li><Link to="/specialty-pharmacy" className="hover:text-white cursor-pointer">専門薬局</Link></li>
                <li><Link to="/sterile-preparation" className="hover:text-white cursor-pointer">無菌調剤</Link></li>
                <li><Link to="/dx" className="hover:text-white cursor-pointer">薬局DX</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="text-lg font-semibold mb-4">情報・リンク</h3>
              <ul className="space-y-2 text-gray-400">
                <li><Link to="/information" className="hover:text-white cursor-pointer">公的機関リンク</Link></li>
                <li><Link to="/career" className="hover:text-white cursor-pointer">転職情報</Link></li>
                <li><Link to="/entertainment" className="hover:text-white cursor-pointer">エンタメ</Link></li>
                <li><Link to="/beast-blog" className="hover:text-white cursor-pointer">ビースト君ブログ</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 みんなの薬局～Pharmacare～. All rights reserved. | <a href="https://readdy.ai/?origin=logo" className="hover:text-white cursor-pointer">Website Builder</a></p>
          </div>
        </div>
      </footer>
    </div>
  );
}
